import React, { useEffect, useState } from "react";

import { FormLabel, TextField } from "@mui/material";
import propTypes from "prop-types";

import ErrorLabel from "../ErrorLabel";
import { inputBoxStyles } from "./input.style";

import CancelIcon from "../../assets/svgIcons/Cancel";

const InputBox = ({
  id,
  labelText,
  name,
  value,
  onChange,
  placeholder,
  errorMsg,
  requiredMsg,
  blurHandler,
  disabled,
  rows,
  customClass = "",
  validator,
  maxLength,
  type,
  onFocus,
  onEscapeKeyDown,
  isAutoFocus,
  hideClearBtn,
  onMaxLengthReached,
  isRequired,
  labelFont,
}) => {
  const classes = inputBoxStyles(this);
  const [inputError, setInputError] = useState();
  const [inputValue, setInputValue] = useState();
  const inputRef = React.useRef(null);
  const [lastValidVal, setLastValue] = useState(value || "");

  useEffect(() => setInputError(errorMsg), [errorMsg]);
  useEffect(() => setInputValue(value), [value]);
  const inputId = id;

  const validateInput = (value) => {
    if (requiredMsg && value && !value.trim()) {
      setInputError(errorMsg || requiredMsg);
    } else {
      setInputError(errorMsg);
    }
  };

  const inputChangeHandler = (event) => {
    let { value, name } = event.target;
    let textValue = value;
    if (validator) {
      if (validator.test(value) || value === "") {
        setLastValue(value);
        setInputValue(value);
      } else {
        textValue = lastValidVal;
        setInputValue(lastValidVal);
      }
    } else {
      setInputValue(value);
    }
    validateInput(textValue);
    if (onChange && onChange instanceof Function) {
      onChange(textValue, name);
    }
  };

  const onBlur = (event) => {
    const { value } = event.target;
    validateInput(value);
    if (blurHandler) blurHandler(event);
  };

  const onTextFocus = (event) => {
    if (onFocus) {
      onFocus(event);
    }
  };

  const clearInput = () => {
    setInputValue("");
    const input = inputRef.current;
    const lastValue = input.value;
    input.value = "";
    const event = new Event("input", { bubbles: true });
    const tracker = input._valueTracker;
    if (tracker) {
      tracker.setValue(lastValue);
    }
    input.dispatchEvent(event);
  };

  const onKeyDown = (event) => {
    if (
      typeof onMaxLengthReached === "function" &&
      inputRef &&
      event &&
      value &&
      maxLength &&
      value.trim().length === maxLength
    ) {
      onMaxLengthReached(maxLength);
    }
  };

  return (
    <>
      <div className={customClass}>
        <FormLabel
          sx={{
            "&.MuiFormLabel-root": {
              color: "black",
              width: "max-content",
              marginBottom: "6px",
              fontSize: labelFont ? "12px" : "14px",
            },
          }}
          htmlFor={inputId}
        >
          {labelText} {isRequired && <span style={{ color: "red" }}>*</span>}
        </FormLabel>
        <TextField
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#636363",
            },
            ".MuiInputBase-input": {
              backgroundColor: "var(--color-white)",
              padding: "2px",
              "&.hover": {
                color: "red",
              },
              "&::placeholder": {
                fontSize: "14px",
              },
            },
            "& .MuiOutlinedInput-root": {
              maxHeight: rows ? "" : "32px",
              "& fieldset": {},
              "&:hover fieldset": {
                boxShadow: "0px 0px 8px var(--color-shadow-primary)",
                border: "0.5px solid var(--color-light-teal)",
              },
              "&.Mui-focused fieldset": {
                boxShadow: "0px 0px 8px var(--color-shadow-primary)",
                border: "0.5px solid var(--color-light-teal)",
              },
              //maxHeight: "32px",
            },
          }}
          InputProps={{
            style: {
              padding: 5,
            },
            disableunderline: "true",
            id: inputId,
            endAdornment: (
              <>
                {!hideClearBtn && (inputValue || inputValue === 0) && (
                  <div className="pointer" onClick={clearInput}>
                    <CancelIcon />
                  </div>
                )}
              </>
            ),
          }}
          disabled={disabled}
          autoComplete="off"
          className={classes.input}
          placeholder={placeholder}
          multiline={Boolean(rows)}
          minRows={rows}
          maxRows={rows}
          name={name}
          id={inputId}
          type={type}
          title=""
          error={Boolean(inputError)}
          value={value}
          required={Boolean(requiredMsg)}
          onChange={inputChangeHandler}
          onBlur={onBlur}
          onFocus={onTextFocus}
          inputRef={inputRef}
          inputProps={{ maxLength }}
          onKeyDown={onEscapeKeyDown ? onEscapeKeyDown : onKeyDown}
          autoFocus={isAutoFocus}
        />
        {inputError && <ErrorLabel message={inputError} />}
      </div>
    </>
  );
};

InputBox.propTypes = {
  type: propTypes.string,
  isAutoFocus: propTypes.bool,
};

InputBox.defaultProps = {
  type: "text",
  isAutoFocus: false,
  showClearBtn: true,
};

export default InputBox;
